import React, { type FC, useState } from "react";

import type { TaskUsageEntry, UsageSummarySnapshot } from "../../types";

import {
  formatTokenCount,
  formatTokenHours,
  formatUsd,
  toFiniteNumber,
} from "../../utils/currency";
import { formatDateTime } from "../../utils/time";
import { Badge } from "../common/Badge";
import { EmptyState } from "../common/EmptyState";
import { FloatingCard } from "../common/FloatingCard";
import { ChevronRightIcon } from "../common/icons";
import { PanelSection } from "../common/PanelSection";
import { Text } from "../common/Text";
import {
  getMissingPricingInputsTitle,
  getUsagePricingStatusText,
  UsageModalityCoveragePanel,
} from "../usage/UsagePricingDetails";

const EMPTY_VALUE = "\u2014";

const PROVIDER_BORDER_CLASSES: Record<string, string> = {
  anthropic: "border-l-orange-400 dark:border-l-orange-500",
  bedrock: "border-l-orange-400 dark:border-l-orange-500",
  claude: "border-l-orange-400 dark:border-l-orange-500",
  codex: "border-l-emerald-400 dark:border-l-emerald-500",
  deepseek: "border-l-sky-400 dark:border-l-sky-500",
  gemini: "border-l-violet-400 dark:border-l-violet-500",
  google: "border-l-violet-400 dark:border-l-violet-500",
  mimo: "border-l-orange-500 dark:border-l-orange-500",
  minimax: "border-l-emerald-400 dark:border-l-emerald-500",
  openai: "border-l-emerald-400 dark:border-l-emerald-500",
  openrouter: "border-l-emerald-400 dark:border-l-emerald-500",
  vertex: "border-l-violet-400 dark:border-l-violet-500",
  xai: "border-l-sky-400 dark:border-l-sky-500",
  xiaomi: "border-l-orange-500 dark:border-l-orange-500",
};

function getProviderBorderClass(provider: string | null | undefined): string {
  if (!provider) {
    return "border-l-slate-400 dark:border-l-slate-500";
  }
  return (
    PROVIDER_BORDER_CLASSES[provider.toLowerCase()] ??
    "border-l-slate-400 dark:border-l-slate-500"
  );
}

interface BreakdownLine {
  label: string;
  tokens: number | null;
  unit: string;
  costUsd: number | null;
}

function getEntryBreakdownLines(entry: TaskUsageEntry): BreakdownLine[] {
  const cb = entry.costBreakdown;
  const {
    promptTokens,
    completionTokens,
    cachedInputTokens,
    cacheWriteTokens,
  } = entry.tokens;
  const lines: BreakdownLine[] = [];

  const uncached = toFiniteNumber(entry.uncachedInputTokens);
  const inputTokens = uncached ?? (promptTokens > 0 ? promptTokens : null);
  const inputCost = toFiniteNumber(cb?.inputUsd);
  if (inputTokens !== null || inputCost !== null) {
    lines.push({
      costUsd: inputCost,
      label: uncached !== null ? "Uncached input" : "Input",
      tokens: inputTokens,
      unit: "tok",
    });
  }

  const outputCost = toFiniteNumber(cb?.outputUsd);
  if (completionTokens > 0 || outputCost !== null) {
    lines.push({
      costUsd: outputCost,
      label: "Output",
      tokens: completionTokens > 0 ? completionTokens : null,
      unit: "tok",
    });
  }

  const cachedCost = toFiniteNumber(cb?.cachedInputUsd);
  if (cachedInputTokens > 0 || cachedCost !== null) {
    lines.push({
      costUsd: cachedCost,
      label: "Cached input",
      tokens: cachedInputTokens > 0 ? cachedInputTokens : null,
      unit: "tok",
    });
  }

  const writeCost = toFiniteNumber(cb?.cacheWriteUsd);
  if (cacheWriteTokens > 0 || writeCost !== null) {
    lines.push({
      costUsd: writeCost,
      label: "Cache write",
      tokens: cacheWriteTokens > 0 ? cacheWriteTokens : null,
      unit: "tok",
    });
  }

  const storageCost = toFiniteNumber(cb?.cacheStorageUsd);
  const storageHours = toFiniteNumber(entry.cacheStorageTokenHours);
  if (storageCost !== null || storageHours !== null) {
    lines.push({
      costUsd: storageCost,
      label: "Cache storage",
      tokens: storageHours,
      unit: "tok-h",
    });
  }

  return lines;
}

function formatBreakdownTokens(line: BreakdownLine): string {
  if (line.tokens === null) {
    return EMPTY_VALUE;
  }
  if (line.unit === "tok-h") {
    return formatTokenHours(line.tokens);
  }
  return formatTokenCount(line.tokens);
}

function extractEntryFields(entry: TaskUsageEntry): {
  stage: string;
  provider: string;
  model: string | null;
  threadId: string | null;
  timestampMs: number | null;
  totalTokens: number | null;
  costUsd: number | null;
} {
  const stage = entry.stage.trim() ? entry.stage : EMPTY_VALUE;
  const provider = entry.provider.trim() ? entry.provider : EMPTY_VALUE;
  const model = entry.model?.trim() ? entry.model : null;
  const threadId = entry.threadId?.trim() ? entry.threadId : null;
  const timestampMs = toFiniteNumber(entry.timestampMs);
  const totalTokens = toFiniteNumber(entry.tokens.totalTokens);
  const costUsd = toFiniteNumber(entry.costUsd);

  return {
    costUsd,
    model,
    provider,
    stage,
    threadId,
    timestampMs,
    totalTokens,
  };
}

const UsageEntryCostCell: FC<{ entry: TaskUsageEntry }> = ({ entry }) => {
  const costUsd = toFiniteNumber(entry.costUsd);
  const pricingStatus = getUsagePricingStatusText(entry.costBreakdown);
  const missingTitle = getMissingPricingInputsTitle(
    entry.costBreakdown?.missingPricingInputs,
  );

  return (
    <div className="flex flex-col items-end">
      <span>{costUsd !== null ? formatUsd(costUsd) : EMPTY_VALUE}</span>
      {pricingStatus ? (
        <span
          className="cursor-default text-[11px] leading-tight text-slate-500 dark:text-slate-400"
          title={missingTitle}
        >
          {pricingStatus}
        </span>
      ) : null}
    </div>
  );
};

const UsageEntriesDesktopTable: FC<{ entries: TaskUsageEntry[] }> = ({
  entries,
}) => {
  const [expandedIds, setExpandedIds] = useState<ReadonlySet<string>>(
    new Set(),
  );

  const toggle = (entryId: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(entryId)) {
        next.delete(entryId);
      } else {
        next.add(entryId);
      }
      return next;
    });
  };

  return (
    <div className="hidden overflow-x-auto sm:block">
      <table className="min-w-full text-left text-xs">
        <thead className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
          <tr>
            <th className="py-2 pr-4 font-medium">Stage</th>
            <th className="py-2 pr-4 font-medium">Provider / Model</th>
            <th className="hidden py-2 pr-4 font-medium md:table-cell">
              Thread
            </th>
            <th className="py-2 pr-4 text-right font-medium">Tokens</th>
            <th className="py-2 pr-4 text-right font-medium">Cost</th>
            <th className="hidden py-2 font-medium lg:table-cell">Timestamp</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 dark:divide-white/10">
          {entries.map((entry) => {
            const expanded = expandedIds.has(entry.entryId);
            return (
              <React.Fragment key={entry.entryId}>
                <UsageEntryRow
                  entry={entry}
                  expanded={expanded}
                  onToggle={() => {
                    toggle(entry.entryId);
                  }}
                />
                {expanded && <UsageEntrySubRows entry={entry} />}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export const UsageBreakdownSection: FC<{
  usageSummary: UsageSummarySnapshot;
}> = ({ usageSummary }) => {
  const normalizedEntries = usageSummary.entries;

  const totalCostUsd = toFiniteNumber(usageSummary.totals.costUsd);
  const { totalTokens } = usageSummary.totals;
  const hasTokens = Number.isFinite(totalTokens);
  const hasTotals = totalCostUsd !== null || hasTokens;
  const pricingCompleteness =
    usageSummary.totals.costBreakdown?.completeness ?? null;
  const missingPricingInputs =
    usageSummary.totals.costBreakdown?.missingPricingInputs ?? [];
  const pricingTooltip = getMissingPricingInputsTitle(missingPricingInputs);
  const hasModalityCoverage =
    Boolean(usageSummary.totals.inputTokensByModality) ||
    Boolean(usageSummary.totals.outputTokensByModality);

  return (
    <PanelSection
      title="Usage Breakdown"
      headerContent={
        hasTotals && (
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="mr-auto font-mono text-[11px] font-bold uppercase tracking-[0.18em] text-slate-900 dark:text-white">
              Usage Breakdown
            </h2>
            {totalCostUsd !== null && (
              <Badge variant="success" size="md">
                Total: {formatUsd(totalCostUsd)}
              </Badge>
            )}
            {hasTokens && (
              <Badge variant="default" size="md">
                Tokens: {formatTokenCount(totalTokens)}
              </Badge>
            )}
            {pricingCompleteness && (
              <span title={pricingTooltip} className="cursor-default">
                <Badge
                  variant={
                    pricingCompleteness === "partial" ? "warning" : "info"
                  }
                  size="md"
                >
                  Pricing: {pricingCompleteness}
                </Badge>
              </span>
            )}
          </div>
        )
      }
      padding="compact"
    >
      {hasModalityCoverage && (
        <div className="mb-3">
          <UsageModalityCoveragePanel
            inputTokensByModality={usageSummary.totals.inputTokensByModality}
            outputTokensByModality={usageSummary.totals.outputTokensByModality}
          />
        </div>
      )}

      {normalizedEntries.length === 0 ? (
        <EmptyState
          title="No usage entries"
          description="No usage entries recorded for this task."
          size="sm"
          className="!border-dashed !py-8"
        />
      ) : (
        <>
          <div className="space-y-3 sm:hidden">
            {normalizedEntries.map((entry) => (
              <UsageEntryCard key={entry.entryId} entry={entry} />
            ))}
          </div>

          <UsageEntriesDesktopTable entries={normalizedEntries} />
        </>
      )}
    </PanelSection>
  );
};

const UsageEntryRow: FC<{
  entry: TaskUsageEntry;
  expanded: boolean;
  onToggle: () => void;
}> = ({ entry, expanded, onToggle }) => {
  const fields = extractEntryFields(entry);
  const providerBorder = getProviderBorderClass(entry.provider);
  const providerDotColor = providerBorder.replace(/border-l-/g, "bg-");

  return (
    <tr
      className="cursor-pointer text-slate-700 transition-colors hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-white/[0.02]"
      onClick={onToggle}
    >
      <td className="whitespace-nowrap py-2 pr-4">
        <span className="inline-flex items-center gap-1.5 font-medium">
          <ChevronRightIcon
            className={`h-3.5 w-3.5 shrink-0 text-gray-400 transition-transform duration-150 dark:text-gray-500 ${expanded ? "rotate-90" : ""}`}
            size={14}
          />
          <span
            aria-hidden="true"
            className={`inline-block h-2 w-2 flex-shrink-0 rounded-none ${providerDotColor}`}
          />
          {fields.stage}
        </span>
      </td>
      <td className="py-2 pr-4">
        <div className="flex flex-col">
          <span className="font-medium">{fields.provider}</span>
          <Text>{fields.model ?? EMPTY_VALUE}</Text>
        </div>
      </td>
      <td className="hidden py-2 pr-4 md:table-cell">
        <Text variant="mono" className="block max-w-[220px] truncate">
          {fields.threadId ?? EMPTY_VALUE}
        </Text>
      </td>
      <td className="whitespace-nowrap py-2 pr-4 text-right font-mono">
        {fields.totalTokens !== null
          ? formatTokenCount(fields.totalTokens)
          : EMPTY_VALUE}
      </td>
      <td className="whitespace-nowrap py-2 pr-4 text-right font-mono">
        <UsageEntryCostCell entry={entry} />
      </td>
      <td className="hidden whitespace-nowrap py-2 lg:table-cell">
        <Text>{formatDateTime(fields.timestampMs)}</Text>
      </td>
    </tr>
  );
};

const UsageEntrySubRows: FC<{ entry: TaskUsageEntry }> = ({ entry }) => {
  const lines = getEntryBreakdownLines(entry);
  if (lines.length === 0) {
    return null;
  }

  return (
    <>
      {lines.map((line) => (
        <tr
          key={line.label}
          className="text-gray-500 dark:text-gray-400 [&>td]:py-1.5"
        >
          <td className="whitespace-nowrap pr-4 pl-7 text-xs">{line.label}</td>
          <td className="pr-4" />
          <td className="hidden pr-4 md:table-cell" />
          <td className="whitespace-nowrap pr-4 text-right font-mono text-xs">
            {formatBreakdownTokens(line)}
          </td>
          <td className="whitespace-nowrap pr-4 text-right font-mono text-xs">
            {line.costUsd !== null ? formatUsd(line.costUsd) : EMPTY_VALUE}
          </td>
          <td className="hidden lg:table-cell" />
        </tr>
      ))}
    </>
  );
};

const UsageEntryCard: FC<{ entry: TaskUsageEntry }> = ({ entry }) => {
  const [expanded, setExpanded] = useState(false);
  const fields = extractEntryFields(entry);
  const pricingStatus = getUsagePricingStatusText(entry.costBreakdown);
  const missingTitle = getMissingPricingInputsTitle(
    entry.costBreakdown?.missingPricingInputs,
  );
  const breakdownLines = getEntryBreakdownLines(entry);
  const borderClass = getProviderBorderClass(entry.provider);

  return (
    <FloatingCard
      padding="lg"
      className={`border-l-[3px] border-slate-300 !bg-white dark:border-slate-700 dark:!bg-white/5 ${borderClass}`}
    >
      <div
        className="flex cursor-pointer items-start justify-between gap-3"
        onClick={() => {
          setExpanded((prev) => !prev);
        }}
      >
        <div className="min-w-0">
          <p className="flex items-center gap-1.5 font-mono text-[11px] font-bold uppercase tracking-[0.16em] text-slate-900 dark:text-white">
            <ChevronRightIcon
              className={`h-3.5 w-3.5 shrink-0 text-gray-400 transition-transform duration-150 dark:text-gray-500 ${expanded ? "rotate-90" : ""}`}
              size={14}
            />
            <span className="truncate">{fields.stage}</span>
          </p>
          <Text as="p" className="truncate pl-5">
            {fields.provider}
            {fields.model ? ` \u00b7 ${fields.model}` : ""}
          </Text>
        </div>
        <div className="text-right">
          <p className="font-mono text-sm font-bold text-slate-900 dark:text-white">
            {fields.costUsd !== null ? formatUsd(fields.costUsd) : EMPTY_VALUE}
          </p>
          <Text as="p" className="font-mono">
            {fields.totalTokens !== null
              ? `${formatTokenCount(fields.totalTokens)} tok`
              : EMPTY_VALUE}
          </Text>
          {pricingStatus ? (
            <p
              className="text-[10px] text-slate-500 dark:text-slate-400"
              title={missingTitle}
            >
              {pricingStatus}
            </p>
          ) : null}
        </div>
      </div>

      {expanded && (
        <>
          {breakdownLines.length > 0 && (
            <div className="mt-2 space-y-1 border-t border-gray-100 pt-2 dark:border-white/5">
              {breakdownLines.map((line) => (
                <Text
                  as="div"
                  key={line.label}
                  className="flex items-center justify-between gap-3 pl-5"
                >
                  <span>{line.label}</span>
                  <span className="flex gap-3 font-mono">
                    <span className="min-w-[4rem] text-right tabular-nums">
                      {formatBreakdownTokens(line)}
                    </span>
                    <span className="min-w-[5rem] text-right tabular-nums">
                      {line.costUsd !== null
                        ? formatUsd(line.costUsd)
                        : EMPTY_VALUE}
                    </span>
                  </span>
                </Text>
              ))}
            </div>
          )}

          <div className="mt-3 border-t border-gray-100 pt-3 dark:border-white/5">
            <div className="grid grid-cols-1 gap-2 text-xs text-gray-600 dark:text-gray-300">
              <div className="flex items-center justify-between gap-3">
                <span className="text-gray-500 dark:text-gray-400">Thread</span>
                <span className="max-w-[220px] truncate font-mono">
                  {fields.threadId ?? EMPTY_VALUE}
                </span>
              </div>
              <div className="flex items-center justify-between gap-3">
                <span className="text-gray-500 dark:text-gray-400">
                  Timestamp
                </span>
                <span className="font-mono">
                  {formatDateTime(fields.timestampMs)}
                </span>
              </div>
            </div>
          </div>
        </>
      )}
    </FloatingCard>
  );
};
